import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, inject, Input } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import { FormlyModule } from "@ngx-formly/core";
import _ from "lodash";
import { MessageService } from "primeng/api";
import { map, Observable, of, take } from "rxjs";
import { INZorroOutput } from "../../../core/models/common";
import { TestCaseGeneratorActions } from "../../../core/ngrx/actions/testcase-generator.actions";
import {
  selectAllTestCases,
  selectAllTestCaseVersions,
  selectError,
  selectGenerating,
  selectLoadingTestCases,
  selectProgressMessages,
  selectVersionIdState
} from "../../../core/ngrx/selectors/testcase-generator.selectors";
import { CommonService } from "../../../core/services/common.service";
import { ExcelExportService } from "../../../core/services/download-excel/excel-export.service";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { UtilitiesService } from "../../../core/services/utilities.service";
import { MaterialModule } from "../../../core/shared/material.module";
import { generatedTestCaseTable } from "../../../core/table-templates/generated-test-cases-table";
import { testCaseTableColumns, testCaseTableMoreDetails } from "../../../core/table-templates/test-case-table";
import { MessageEnum } from "../../../core/utils/message.enum";
import { AddTestSuiteComponent } from "../../modal-templates/add-test-suite/add-test-suite.component";
import { SharedComponents } from "../../shared.module";
import { TestCaseTableComponent } from "../test-case-table/test-case-table.component";
import { TcgFilterModalComponent } from "../../modal-templates/tcg-filter-modal/tcg-filter-modal.component";
import { userstoryDetails } from "../../../core/dummy/dummy-data";
import { TcgStoryDetailModalComponent } from "../../modal-templates/tcg-story-detail-modal/tcg-story-detail-modal.component";
import { TcgTraceabilityModalComponent } from "../../modal-templates/tcg-traceability-modal/tcg-traceability-modal.component";

/**
 * @class GeneratedTestCasesComponent
 * @description Component responsible for displaying and managing generated test cases.
 *              It includes functionality for loading test cases, showing progress,
 *              handling user feedback, and exporting test case data.
 *
 * @selector app-generated-test-cases
 */
@Component({
  selector: "app-generated-test-cases",
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    FormlyModule,
    FormlyBootstrapModule,
    TestCaseTableComponent,
    ...SharedComponents,
  ],
  templateUrl: "./generated-test-cases.component.html",
  styleUrl: "./generated-test-cases.component.scss",
})
export class GeneratedTestCasesComponent {
  /**
   * @property {any} data - Input property to receive data for initializing the component.
   */
  @Input() data!: any;
  private modalService = inject(NgbModal);

  private messageService = inject(MessageService);
  /**
   *  Observable stream of test cases.
   */
  testCases$!: Observable<any[]>;
  loadingTestCases$!: Observable<boolean>;
  error$!: Observable<string | null>;
  generating$!: Observable<boolean>;
  tableDataSet$!: Observable<any[]>;
  isGenerate: boolean = false;
  isGenerating = true;
  previousData:any = null;

  /**
   * @property {Object} tableData - Object containing table configuration and data.
   */
  tableData: {
    headers?: string;
    columns: any[];
    data: Observable<any[]>;
  } = {
    headers: "",
    columns: [],
    data: of([]),
  };
  projectId: string;
  showNoData: boolean = false;
  feedbackList: any = [];
  editedData: any = [];
  progressText: string = "";
  percentage: number = 0;
  originalRows:any=[];

  updatedAndDisplayData: any;

  /**
   * @property {Object[]} progressMessages - List of progress messages with corresponding steps and time delays.
   */
  progressMessages: any = [
    {
      message: "Initializing",
      steps: [
        {
          time: 1000,
          percentage: 2,
        },
        {
          time: 3000,
          percentage: 5,
        },
        {
          time: 3000,
          percentage: 10,
        },
      ],
    },
    {
      message: "Generating Test Cases",
      steps: [
        {
          time: 4000,
          percentage: 25,
        },
        {
          time: 3000,
          percentage: 39,
        },
        {
          time: 4000,
          percentage: 71,
        },
      ],
    },
    {
      message: "Generating Test Cases",
      steps: [
        {
          time: 8000,
          percentage: 80,
        },
        {
          time: 5000,
          percentage: 85,
        },
        {
          time: 6000,
          percentage: 89,
        },
      ],
    },
  ];
  isUpdateFeedback: boolean = false;
  items: any;
  // to send the selected rows
  selectedRow: any;
  editRows: any;
  llmConnection: any;
  versionId: any;
// load test suite name
  testCasesByVersionId$!: Observable<any[]>;
  testCaseVersions$!: Observable<any[]>;
  isLoading: boolean = false;
  
  tableConfigs: any = {
    columns: [],
    rows: [],
    titleKey: "testScenario",
    isEdit: false,
    isFeedback: true, 
  };
  progress: any = {
    text: 'Initializing',
    percentage: 5
  };

  /**
   * @constructor
   * @param {Store} store - State management store service.
   * @param {ActivatedRoute} route - Activated route for accessing route parameters.
   * @param {ChangeDetectorRef} cdr - Change detector for manual view updates.
   * @param {UtilitiesService} utilitiService - Utility service for miscellaneous functions.
   * @param {ExcelExportService} excelExportService - Service for exporting data to Excel.
   */
  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private utilitiService: UtilitiesService,
    private excelExportService: ExcelExportService,
    private restService: RestService,
    private commonService: CommonService,
    private readonly toaster: ToasterService
  ) {
    this.projectId = this.route.snapshot.paramMap.get("id") as string;

    // Load Store Data and set values
    this.loadStoreData();
  }

  /**
   *  Initializes the component by setting up observables, progress tracking, and data loading.
   */
  ngOnInit() {
    if(this.tableConfigs.rows?.length) this.isGenerating = false;
    this.feedbackList = [];
    this.tableData = this.getData();
    this.generating$.subscribe((response: any) => {
      if (!response) {
        this.percentage = 100;
        setTimeout(() => {
          this.isGenerating = false;
        }, 2500);
      }
    });
    // this.loopProgressMessage(0);

    this.testCases$.subscribe((item) => {
      this.feedbackList = [];
      this.isUpdateFeedback = false;
    });

    this.error$.subscribe((item) => {
      this.feedbackList = [];
      this.isUpdateFeedback = false;
    });

    this.loadTestSuiteData();
  }
 

  loadTestSuiteData() {
    this.store.dispatch(
      TestCaseGeneratorActions.loadTestCaseVersions({
        projectId: this.projectId,
      })
    );

    this.testCaseVersions$ = this.store.select(selectAllTestCaseVersions).pipe(
      map((connections) =>
        connections.map((connection: any) => ({
          value: connection,
          label: connection.versionName,
        }))
      )
    );
   
  }
  /**
   *  Handles looping through progress messages.
   * @param {number} index - Index of the current progress message.
   */
  loopProgressMessage(index: number) {
    // this.loopSteps(this.progressMessages[index].steps, 0, index);
  }

  /**
   *  Handles the steps for progress messages.
   * @param {Object[]} steps - Array of step objects with time and percentage.
   * @param {number} index - Index of the current step.
   * @param {number} parentIndex - Index of the parent progress message.
   */
  loopSteps(
    steps: { time: number; percentage: number }[],
    index: number,
    parentIndex: number
  ) {
    setTimeout(() => {
      this.percentage = steps[index].percentage;
      this.progressText = this.progressMessages[parentIndex].message;
      if (steps[index + 1]) {
        this.loopSteps(steps, index + 1, parentIndex);
      } else if (this.progressMessages[parentIndex + 1]) {
        this.loopProgressMessage(parentIndex + 1);
      }
    }, steps[index].time);
  }

  /**
   * Loads test case-related observables from the store.
   */
  loadStoreData() {
    this.testCases$ = this.store.select(selectAllTestCases);

    this.store.select(selectVersionIdState).subscribe((versionId: any) => {
      this.versionId = versionId;
    });

    this.store.select(selectAllTestCases).subscribe((res: any) => {
      this.tableConfigs.rows = _.cloneDeep(res);
      this.originalRows = [...this.tableConfigs.rows];
      this.tableConfigs = {
        ...this.tableConfigs,
        rows: [...this.tableConfigs.rows]
      };
    });

    this.store.select(selectProgressMessages).subscribe((item: any) => {
      this.progress.text = item.message ? item.message : this.progress.text;
      this.progress.isError = !!item.isError;
      if(item.totalPaths) this.progress.percentage = Math.round(item.processingPaths/item.totalPaths*100);
      if(this.progress.percentage >= 100 && item?.status === 'COMPLETED') {
        setTimeout(() => {
          this.isGenerating = false;
          this.isLoading = true;
        }, 3000);
        setTimeout(() => {
          this.isLoading = false;
        }, 17000);
      }
    })

    this.loadingTestCases$ = this.store.select(selectLoadingTestCases);
    this.generating$ = this.store.select(selectGenerating);
    this.error$ = this.store.select(selectError);

  }

  getFormFields() {
 
      return [
        {
          fieldGroupClassName: "row",
          fieldGroup: [
            {
              className: "col-md-12",
              key: "testSuiteName",
              type: "input",
              templateOptions: {
                label: "Test Suite Name",
                required: true,
              },
              validation: {
                messages: {
                  required: () => "This field is required.",
                },
              }
            }
          ]
        }
      ];
    
  }
  /**
   * Dispatches an action to update test cases with user feedback.
   */
  onPublish() {
    this.store.dispatch(TestCaseGeneratorActions.clearTestCaseVersions());
    let testCases: any = [];
    this.selectedRow = undefined;
    this.feedbackList.forEach((item: any) => {
      testCases.push({
        projectId: this.projectId,
        testcaseId: item.testCaseId,
        userInput: item.feedback,
        versionId: this.versionId,
      });
    });
    this.isUpdateFeedback = true;
    this.store.dispatch(
      TestCaseGeneratorActions.updateTestCase({
        testCases,
        projectId: this.projectId,
      })
    );
  }

  
  addtestSuite() {
    // to recevice llm
    this.commonService.llmConnection$.subscribe((connection) => {
      this.llmConnection = connection;
    });

     // to recevice version Id
    this.store.select(selectVersionIdState).subscribe((versionId: any) => {
      this.versionId = versionId;
        });
        if (!this.selectedRow) {
          this.toaster.pop({ type: 'error', text: MessageEnum.DATA_SELECTION_NONE});
          return;
        }
    
    let modalRef = this.modalService.open(AddTestSuiteComponent, {
      centered: true,
      // size: 'lg',
      backdrop: 'static',
      windowClass: "add-item-modal",
    });
    modalRef.componentInstance.data = {
      fields: this.getFormFields(),
    };
    modalRef.result
    .then((result: any) => {
      if(result) {
        if(result?.testSuite)
        {
          
          this.testCaseVersions$.pipe(take(1)).subscribe((versionConnections) => {
            // Check if the entered version name already exists
            const versionExists = versionConnections.some(
              (version) => version.label === result?.testSuite
            );
          
            if (versionExists) {
              this.toaster.pop({ type: 'error', text: MessageEnum.TEST_SUITE_NAME_DUPLICATE });
              // Keep the modal open by resetting the modal result
              modalRef.dismiss(); // Close current modal
              this.addtestSuite(); // Reopen modal
              return; // Stop execution, don't call the API
            }
          
            // Proceed with API call if the name is unique
            let formattedData = {
              _id: this.versionId,
              versionName: result?.testSuite || '',
              selectedVersionId: null,
              isNewTestSuite: !!result?.testSuite, // true if versionName exists
              llmConnectionId: this.llmConnection,
              testCases: this.selectedRow?.map((testCase: any) => ({
                testCaseId: testCase.testCaseId,
                testCase: testCase.testCase,
                testScenario: testCase.testScenario,
                expectedResult: testCase.expectedResult,
                prerequisites: testCase.prerequisites,
                steps: testCase.steps,
                _id: testCase._id,
                key: testCase.key,
                storydesc: testCase.storydesc,
                storysummary: testCase.storysummary,
                acceptancecriteria: testCase.acceptancecriteria ?? testCase.acceptanceCriteria,
              })) || [],
            };
          
            this.restService.persistTestCase(this.projectId, formattedData).subscribe({
              next: (response: any) => {
                if (response?.status === 201) {
                  this.toaster.pop({ type: 'success', text: MessageEnum.TEST_SUITE_CREATION_SUCCESS});
                  this.commonService.notifyVersionUpdate();
                }
              },
              error: (error: any) => {
                console.error("Persist API Error:", error);
                this.toaster.pop({ type: 'error', text: MessageEnum.TEST_SUITE_CREATION_FAILURE});
              },
            });
          });
          
      }
      }
    });
  }
  
  /**
   * Exports test case data to an Excel file.
   */
  onDownload() {
    if (this.selectedRow?.length) {
      const formattedData = this.selectedRow?.map((row: any) => ({
        "_id": row._id,
        "key" : row.key,
        "storydesc": row.storydesc,
        "testCaseId": row.testCaseId,
        "testCase": row.testCase,
        "prerequisites": row.prerequisites,
        "testScenario": row.testScenario,
        "expectedResult": row.expectedResult,
        storysummary:row?.storysummary || '',
        // priority:row?.priority || '',
        testCaseType:row?.testCaseType || row?.testType || '',
        subType:row?.subType || '',
      }));

      const timestamp = new Date().toISOString();
      const sheetName = "Generated_Test_Cases_" + timestamp;
      const payload = {
        testSuiteName: sheetName,
        selectedTestCaseCount: formattedData.length,
        testCaseReportDetails : formattedData
      }

      this.restService.downloadExcel(this.projectId, payload).subscribe({
        next: (response: any) => {
          window.open(response?.data, "_blank");
        },
        error: (error: any) => {
          let errMessage = error?.message ? error?.message : MessageEnum.EXCEL_DOWNLOAD_FAILURE;
          this.toaster.pop({ type: 'error', text: errMessage});
        },
      });
    }
  }

  /**
   * Handles table action events such as adding or removing feedback items.
   * @param {INZorroOutput} event - Event object containing table action details.
   */
  handleTableActions(event: INZorroOutput) {
    const editedData = event.data.row;
    this.updatedAndDisplayData = event.data.updatedDisplayData;
    this.editRows = this.editRows || [];
    
    // Find if the testCaseId already exists in the array
    const existingIndex = this.editRows.findIndex((item:any) => item.testCaseId === editedData.testCaseId);
    
    const updatedRow = {
      testCaseId: editedData.testCaseId,
      versionId: editedData._id,
      userStory: editedData.storydesc,
      testCase: editedData.testCase,
      prerequisites: editedData.prerequisites,
      testScenario: editedData.testScenario,
      expectedResult: editedData.expectedResult
    };
    
    if (existingIndex !== -1) {
      // If the testCaseId exists, replace the existing object
      this.editRows = [
        ...this.editRows.slice(0, existingIndex),  
        updatedRow, 
        ...this.editRows.slice(existingIndex + 1) 
      ];
    
    } else {
      // If the testCaseId doesn't exist, append the new object
      this.editRows = [...this.editRows, updatedRow];
    }
    
    
    if (!event?.data?.row?.feedback) {
      const index = this.feedbackList.findIndex(
        (item: any) => item._id === event?.data?.row?._id
      );
      if (index > -1) {
        this.feedbackList.splice(index, 1);
      }
      return;
    }

    const isExistingData = this.feedbackList.find(
      (item: any) => item._id === event?.data?.row?._id
    );
    if (isExistingData) {
      const index = this.feedbackList.findIndex(
        (item: any) => item._id === event?.data?.row?._id
      );
      if (index > -1) {
        this.feedbackList.splice(index, 1);
      }
      this.feedbackList.push(event?.data?.row);
    } else {
      this.feedbackList.push(event?.data?.row);
    }
  }
  
  handleSidePanelActions(event:any){
    if(event.key === 'feedback' && event.data !=""){
      const isExistingData = this.feedbackList.find(
        (item: any) => item._id === event?.data?._id
      );
      if (isExistingData) {
        const index = this.feedbackList.findIndex(
          (item: any) => item._id === event?.data?._id
        );
        if (index > -1) {
          this.feedbackList.splice(index, 1);
        } 
        if(!event.data?.isThumbUp){
          this.feedbackList.push(event?.data);
        }
      } else {
        if(event.data?.feedback !== '' && !event.data?.isThumbUp){
          this.feedbackList.push(event?.data);
        }
      }
    } else if(event.type === 'open-filter'){
      this.openFilter();
    } else if(event.type === 'open-user-story'){
      this.openUserStory(event?.data);
      console.log(event?.type);
    } else if(event.type === 'open-traceability'){
      this.openTraceability(event?.data);
      console.log(event?.type);
    }
  }

  handleRowSelection(event: any) {
    this.selectedRow = [];
    if(event.data) {
      this.selectedRow = event.data.filter((item: any) => item.select);
    }
}


  /**
   *  Returns the configuration and data for the table.
   * @returns {Object} Table configuration and data.
   */
  getData() {
    this.tableConfigs = {
          columns: testCaseTableColumns,
          rows: this.tableConfigs.rows || [],
          titleKey: "testCase",
          isEdit: false,
          isFeedback: true,
          moreDetails: testCaseTableMoreDetails
        }
    return {
      headers: "Generated Test Cases",
      columns: generatedTestCaseTable,
      data: this.testCases$,
    };
  }

  openFilter(){
    let modalRef = this.modalService.open(TcgFilterModalComponent, {
      centered: true,
      size: 'm',
      backdrop: 'static',
    });
    console.log("Test cases list -> ", this.testCases$);
    modalRef.componentInstance.userStories =  this.originalRows?.map((tc: any) => ({
      label: tc.key,
      value: tc.key
    }));;

    modalRef.componentInstance.previousData = this.previousData;

    modalRef.result.then((result: any) => {
      this.filterData(result)
    });
  }

  filterData(data:any){
    if (!data?.story && !data?.priority) {
      this.previousData = null;
      this.tableConfigs.rows = [...this.originalRows];
      return;
    }
    this.previousData = {
      story : data?.story,
      priority : data?.priority
    };

    this.tableConfigs.rows = this.originalRows?.filter((tc:any) => {
    const storyMatch =
      !data?.story ||
      tc.key === data?.story;

    const priorityMatch =
      !data?.priority ||
      tc.priority === data?.priority;
      return storyMatch && priorityMatch;
    });
  }

  openUserStory(data:any){
    const modalRef = this.modalService.open(TcgStoryDetailModalComponent, {
      centered: true,
      windowClass: "user-story-modal",
      size: "xl",
      backdrop: 'static',
    });
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.contentList = userstoryDetails;
    modalRef.componentInstance.title = "User Story Details";
  }

  openTraceability(data:any){
    const modalRef = this.modalService.open(TcgTraceabilityModalComponent, {
      centered: true,
      windowClass: "user-story-modal",
      size: "m",
      backdrop: 'static',
    });
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.title = "Traceability";
  }
}
